/**
 * LA GÉOMÉTRIE DE LA CARTE. Coordonnées axiales → points d'un polygone, et rien d'autre.
 *
 * Le serveur renvoie des `(q, r)` (§4.1) : c'est au dessin de savoir ce que ça vaut en pixels.
 * Hexagones POINTE EN HAUT — c'est l'orientation des cartes dessinées à la main, les rangées y
 * courent horizontalement et une région se lit comme une bande de terrain.
 *
 *   x = taille · √3 · (q + r/2)      y = taille · 3/2 · r
 *
 * Fonction pure, sans React : la carte est le composant le plus long à mettre au point (design §6),
 * autant pouvoir en tester la moitié sans rien monter.
 */
import type { HexagoneEnVue } from "../api/types.js";

export interface CaseDessinee<T> {
  hex: T;
  cx: number;
  cy: number;
  /** Les six sommets, prêts pour un `points=` de `<polygon>`. */
  points: string;
}

export interface CarteDessinee<T> {
  cases: CaseDessinee<T>[];
  /** Le `viewBox` qui contient tout, marge comprise. */
  vue: { x: number; y: number; largeur: number; hauteur: number };
}

const RACINE_DE_TROIS = Math.sqrt(3);

export function centreDe(q: number, r: number, taille: number): { cx: number; cy: number } {
  return { cx: taille * RACINE_DE_TROIS * (q + r / 2), cy: taille * 1.5 * r };
}

export function sommetsDe(cx: number, cy: number, taille: number): string {
  const points: string[] = [];
  for (let i = 0; i < 6; i += 1) {
    // −90° pour la pointe en haut ; le pas est de 60°.
    const angle = (Math.PI / 180) * (60 * i - 90);
    points.push(`${arrondi(cx + taille * Math.cos(angle))},${arrondi(cy + taille * Math.sin(angle))}`);
  }
  return points.join(" ");
}

function arrondi(valeur: number): number {
  // Deux décimales : au-delà, on alourdit le DOM d'une aventure à trois cents hexagones sans qu'un
  // œil humain puisse en voir la différence.
  return Math.round(valeur * 100) / 100;
}

export function disposeLaCarte<T extends { q: number; r: number }>(
  hexagones: readonly T[],
  taille = 30,
  marge = taille
): CarteDessinee<T> {
  const cases = hexagones.map((hex) => {
    const { cx, cy } = centreDe(hex.q, hex.r, taille);
    return { hex, cx, cy, points: sommetsDe(cx, cy, taille) };
  });

  if (cases.length === 0) return { cases, vue: { x: 0, y: 0, largeur: 1, hauteur: 1 } };

  const largeurDemi = (taille * RACINE_DE_TROIS) / 2;
  const xs = cases.map((c) => c.cx);
  const ys = cases.map((c) => c.cy);
  const x = Math.min(...xs) - largeurDemi - marge;
  const y = Math.min(...ys) - taille - marge;
  return {
    cases,
    vue: {
      x: arrondi(x),
      y: arrondi(y),
      largeur: arrondi(Math.max(...xs) + largeurDemi + marge - x),
      hauteur: arrondi(Math.max(...ys) + taille + marge - y)
    }
  };
}

/** Le brouillard, dans l'ordre où il se lit : inconnu (absent de la réponse), aperçu, visité. */
export function estLisible(hex: HexagoneEnVue): boolean {
  return hex.brouillard === "visite";
}

/**
 * LE NOM DE CHAQUE CASE, PAR SA CLEF. Une annotation ne porte que la clef de son hexagone
 * (`h_042`) : c'est ce que la base range, et c'est ce qu'un joueur cite quand il signale un bogue.
 * Mais une liste de notes rangées sous des clefs est une liste illisible — le nom du lieu est la
 * seule chose qui relie une ligne à une case du dessin.
 *
 * Seules les cases VISITÉES ont un nom (§4.1) : une silhouette aperçue de loin n'en donne pas, et
 * l'on n'en invente pas.
 */
export function nomsDesCases(hexagones: readonly HexagoneEnVue[]): Map<string, string> {
  const noms = new Map<string, string>();
  for (const hex of hexagones) if (hex.nom) noms.set(hex.clef, hex.nom);
  return noms;
}
